export const toPerformanceArray = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'object') return Object.values(raw).filter(Boolean);
    return [];
    };
    
    
    export const pickFlow = (p) => Number(p?.airFlow ?? p?.airflow_m3h ?? p?.air_flow ?? 0);
    export const pickPressure = (p) => Number(p?.staticPressure ?? p?.static_pressure ?? p?.static_pressure_pa ?? 0);
    
    
    export const getShaftPower = (d) => d?.shaftPower ?? d?.shaft_power ?? d?.power ?? d?.shaft_power_kw ?? d?.shaftPowerKw ?? null;
    export const pickShaftPower = (p) => {
    const raw = getShaftPower(p);
    if (raw === null || raw === undefined) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
    };
    
    
    export const findNearestPoint = (performanceData, inputPoint) => {
    if (!performanceData || !inputPoint) return null;
    const dataArray = toPerformanceArray(performanceData);
    if (dataArray.length === 0) return null;
    
    
    let nearest = null;
    let minDistance = Infinity;
    
    
    dataArray.forEach((point) => {
    const af = pickFlow(point);
    const sp = pickPressure(point);
    if (Number.isNaN(af) || Number.isNaN(sp)) return;
    const distance = Math.hypot(af - inputPoint.airFlow, sp - inputPoint.staticPressure);
    if (distance < minDistance) {
    minDistance = distance;
    nearest = point;
    }
    });
    
    
    return nearest;
    };
    
    
    // choose motor heuristics: smallest nominal > required
    export const chooseMotorForFan = (motors, maxShaftPower) => {
    if (!Array.isArray(motors) || motors.length === 0) return null;
    const candidates = motors
    .map((m) => ({ motor: m, nominal: Number(m?.nominal_motor_power ?? m?.nominalMotorPower ?? m?.power_kw ?? m?.power ?? NaN) }))
    .filter((x) => Number.isFinite(x.nominal) && x.nominal > Number(maxShaftPower)); // must be greater than shaft power
    if (candidates.length === 0) return null;
    candidates.sort((a, b) => a.nominal - b.nominal);
    return candidates[0].motor;
    };

    // Interpolate performance data at a given point (airFlow, staticPressure)
    // Returns an object with interpolated values including shaft power
    export const interpolatePerformanceAtPoint = (performanceData, targetFlowM3H, targetPressurePa) => {
    if (!performanceData || !Array.isArray(performanceData) || performanceData.length === 0) return null;
    if (targetFlowM3H === null || targetFlowM3H === undefined || targetPressurePa === null || targetPressurePa === undefined) return null;
    
    const dataArray = toPerformanceArray(performanceData);
    if (dataArray.length === 0) return null;
    
    // Sort by flow for easier interpolation
    const sorted = [...dataArray].sort((a, b) => pickFlow(a) - pickFlow(b));
    
    // Find the segment where targetFlowM3H falls
    for (let i = 0; i < sorted.length - 1; i++) {
    const p1 = sorted[i];
    const p2 = sorted[i + 1];
    
    const flow1 = pickFlow(p1);
    const flow2 = pickFlow(p2);
    
    if (targetFlowM3H >= flow1 && targetFlowM3H <= flow2) {
    // Found the segment, interpolate
    if (flow1 === flow2) {
    // Same flow, return the point with closest pressure
    const press1 = pickPressure(p1);
    const press2 = pickPressure(p2);
    const dist1 = Math.abs(press1 - targetPressurePa);
    const dist2 = Math.abs(press2 - targetPressurePa);
    const closest = dist1 <= dist2 ? p1 : p2;
    
    return {
    airFlow: targetFlowM3H,
    airflow_m3h: targetFlowM3H,
    air_flow: targetFlowM3H,
    staticPressure: targetPressurePa,
    static_pressure: targetPressurePa,
    static_pressure_pa: targetPressurePa,
    shaftPower: getShaftPower(closest),
    shaft_power: getShaftPower(closest),
    power: getShaftPower(closest),
    shaft_power_kw: getShaftPower(closest),
    shaftPowerKw: getShaftPower(closest),
    };
    }
    
    // Linear interpolation
    const t = (targetFlowM3H - flow1) / (flow2 - flow1);
    
    const press1 = pickPressure(p1);
    const press2 = pickPressure(p2);
    const power1 = pickShaftPower(p1);
    const power2 = pickShaftPower(p2);
    
    // Interpolate pressure (should be close to targetPressurePa, but we interpolate anyway)
    const interpolatedPressure = press1 + (press2 - press1) * t;
    
    // Interpolate shaft power
    // If both points have power, interpolate linearly
    // If only one has power, use that one
    // If neither has power, try to get it from the nearest point
    let interpolatedPower = null;
    if (power1 !== null && power2 !== null) {
      // Both have power - linear interpolation
      interpolatedPower = power1 + (power2 - power1) * t;
    } else if (power1 !== null) {
      // Only p1 has power - use it
      interpolatedPower = power1;
    } else if (power2 !== null) {
      // Only p2 has power - use it
      interpolatedPower = power2;
    } else {
      // Neither has power - search all points for the nearest one with power
      for (const point of sorted) {
        const power = pickShaftPower(point);
        if (power !== null) {
          interpolatedPower = power;
          break; // Use first available power as fallback
        }
      }
    }
    
    return {
    airFlow: targetFlowM3H,
    airflow_m3h: targetFlowM3H,
    air_flow: targetFlowM3H,
    staticPressure: targetPressurePa, // Use target pressure (intersection point)
    static_pressure: targetPressurePa,
    static_pressure_pa: targetPressurePa,
    shaftPower: interpolatedPower,
    shaft_power: interpolatedPower,
    power: interpolatedPower,
    shaft_power_kw: interpolatedPower,
    shaftPowerKw: interpolatedPower,
    };
    }
    }
    
    // If not found in any segment, find the closest point
    // Try to find the closest point with shaft power first
    let closest = null;
    let closestWithPower = null;
    let minDistance = Infinity;
    let minDistanceWithPower = Infinity;
    
    for (const point of sorted) {
    const flow = pickFlow(point);
    const press = pickPressure(point);
    const distance = Math.hypot(flow - targetFlowM3H, press - targetPressurePa);
    const power = pickShaftPower(point);
    
    if (distance < minDistance) {
    minDistance = distance;
    closest = point;
    }
    
    // Also track the closest point that has power
    if (power !== null && distance < minDistanceWithPower) {
    minDistanceWithPower = distance;
    closestWithPower = point;
    }
    }
    
    // Prefer using the closest point with power
    const finalClosest = closestWithPower || closest;
    
    if (finalClosest) {
    const finalPower = getShaftPower(finalClosest);
    return {
    airFlow: targetFlowM3H,
    airflow_m3h: targetFlowM3H,
    air_flow: targetFlowM3H,
    staticPressure: targetPressurePa,
    static_pressure: targetPressurePa,
    static_pressure_pa: targetPressurePa,
    shaftPower: finalPower,
    shaft_power: finalPower,
    power: finalPower,
    shaft_power_kw: finalPower,
    shaftPowerKw: finalPower,
    };
    }
    
    return null;
    };